import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.filescad.app";

const NAV = [
    { label: "About", to: "/about.html", testId: "nav-about" },
    { label: "FAQ", to: "/faq.html", testId: "nav-faq" },
    { label: "Download by PIN", to: "/download", testId: "nav-download" },
    { label: "Contact", to: "/contact-us.html", testId: "nav-contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isHome = pathname === "/";

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled || !isHome
                    ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-2.5 group"
                >
                    <div className="relative">
                        <img
                            src="https://play-lh.googleusercontent.com/zqPvmjZJnHrvIqHfnpWWhkAAQF4LiYtAor5yPuolfibCLmINaN6VXEa5xDVqD9JDjaUd0YoJ3pXz2tzKGZSObw=w240-h480-rw"
                            alt="FilesCAD"
                            className="w-9 h-9 rounded-xl ring-1 ring-slate-200 group-hover:ring-blue-500 transition"
                        />
                    </div>
                    <div className="leading-none">
                        <div className="font-display font-black text-lg tracking-tight text-slate-950">
                            FilesCAD
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
                            CNC Designs
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((n) => (
                        <Link
                            key={n.to}
                            to={n.to}
                            data-testid={n.testId}
                            className={`text-sm font-medium transition ${
                                pathname === n.to
                                    ? "text-blue-600"
                                    : "text-slate-700 hover:text-slate-950"
                            }`}
                        >
                            {n.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={PLAY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-download-btn"
                        className="hidden sm:inline-flex items-center gap-2 rounded-full bg-slate-950 text-white px-5 py-2.5 text-sm font-semibold hover:bg-blue-600 transition-all hover:-translate-y-0.5"
                    >
                        <Download className="w-4 h-4" />
                        Get the App
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl">
                    <div className="px-6 py-4 flex flex-col gap-3">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-${n.testId}`}
                                className="text-base font-medium text-slate-800 py-1"
                            >
                                {n.label}
                            </Link>
                        ))}
                        <a
                            href={PLAY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-download-btn"
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 text-white px-5 py-3 text-sm font-semibold"
                        >
                            <Download className="w-4 h-4" />
                            Download on Google Play
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
